import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function BlogIndex() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title =
      "Journal — Nightlife & Hospitality Notes by Ritesh Sikri | Rubiemaze Inc";
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/posts`);
        if (!cancelled) setPosts(res.data);
      } catch (err) {
        if (!cancelled) setError("Couldn't load posts. Try again shortly.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      data-testid="blog-index"
      className="relative min-h-screen bg-[#030303] pt-32 md:pt-40 pb-20 md:pb-32"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14 md:mb-20">
          <div className="lg:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-5">
              The Rubiemaze Journal
            </div>
            <h1 className="font-display text-white text-[36px] sm:text-5xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-[-0.02em]">
              Field notes on{" "}
              <em className="font-editorial italic text-[#D4AF37] font-light">
                nightlife economics.
              </em>
            </h1>
          </div>
          <div className="lg:col-span-4 text-white/60 text-sm md:text-base leading-relaxed lg:pl-6">
            Long-form essays on profitability, menu engineering, staffing,
            audits and pre-launch strategy — from the founder of Rubiemaze
            Inc,{" "}
            <span className="text-white/80">Ritesh Sikri</span>.
          </div>
        </div>

        {loading && (
          <div className="text-white/50 text-sm">Loading essays…</div>
        )}
        {error && !loading && (
          <div className="text-red-300 text-sm" data-testid="blog-error">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {posts.map((p, i) => (
            <motion.article
              key={p.slug}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.08 }}
              data-testid={`blog-card-${p.slug}`}
              className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/8 bg-white/[0.02] backdrop-blur-xl hover:border-[#D4AF37]/40 transition-colors duration-500"
            >
              <Link to={`/blog/${p.slug}`} className="block">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={p.cover_image}
                    alt={p.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-white/10 text-[10px] uppercase tracking-[0.2em] text-white/80">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                    Essay
                  </div>
                </div>

                <div className="p-6 md:p-8 flex flex-col flex-1">
                  <div className="text-[11px] uppercase tracking-[0.24em] text-white/45 flex items-center gap-3">
                    <span>{formatDate(p.published_at)}</span>
                    <span className="text-white/25">·</span>
                    <span>{p.reading_time} min read</span>
                  </div>
                  <h2 className="mt-4 font-display text-white text-xl sm:text-2xl md:text-[26px] leading-tight group-hover:text-[#D4AF37] transition-colors">
                    {p.title}
                  </h2>
                  <p className="mt-4 text-white/60 text-sm md:text-[15px] leading-relaxed line-clamp-3">
                    {p.excerpt}
                  </p>
                  <div className="mt-6 pt-4 border-t border-white/8 flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      {(p.tags || []).slice(0, 2).map((t) => (
                        <span
                          key={t}
                          className="text-[10px] uppercase tracking-[0.2em] text-white/40 border border-white/10 rounded-full px-2 py-1"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <span className="text-[#D4AF37] text-sm inline-flex items-center gap-1">
                      Read
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M5 12h14M13 5l7 7-7 7"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
